import { Organization, Person, Product, Review } from 'schema-dts';

import { stripHtml } from '../textUtils';
import type { NonStringThing } from './structuredData.types';

export interface TestimonialSchemaInput {
  name: string;
  title?: string;
  quote: string;
  image?: string;
  imageAlt?: string;
  rating?: number;
  date?: string;
}

export interface TestimonialsSchemaGeneratorProps {
  testimonials: TestimonialSchemaInput[];
  organizationName?: string;
  organizationUrl?: string;
  productName?: string;
  productUrl?: string;
}

export const generateTestimonialsSchema = ({
  testimonials,
  organizationName = 'Storyblocks',
  organizationUrl = 'https://www.storyblocks.com',
  productName,
  productUrl,
}: TestimonialsSchemaGeneratorProps): NonStringThing[] => {
  if (!testimonials || testimonials.length === 0) {
    return [];
  }

  const organization: Organization = {
    '@type': 'Organization',
    name: organizationName,
    url: organizationUrl,
  };

  const product: Product | undefined = productName
    ? {
        '@type': 'Product',
        name: productName,
        url: productUrl || organizationUrl,
        manufacturer: organization,
      }
    : undefined;

  const reviews: Review[] = testimonials.map((testimonial, index) => {
    const author: Person = {
      '@type': 'Person',
      name: testimonial.name,
    };

    if (testimonial.title) {
      author.jobTitle = testimonial.title;
    }

    if (testimonial.image) {
      author.image = {
        '@type': 'ImageObject',
        url: testimonial.image,
        caption: testimonial.imageAlt || `Photo of ${testimonial.name}`,
      };
    }

    const review: Review = {
      '@type': 'Review',
      '@id': `#review-${index + 1}`,
      author,
      reviewBody: stripHtml(testimonial.quote),
      datePublished: new Date(testimonial.date ?? new Date())
        .toISOString()
        .split('T')[0],
    };

    if (
      testimonial.rating &&
      testimonial.rating >= 1 &&
      testimonial.rating <= 5
    ) {
      review.reviewRating = {
        '@type': 'Rating',
        ratingValue: testimonial.rating,
        bestRating: 5,
        worstRating: 1,
      };
    }

    if (product) {
      review.itemReviewed = product;
    } else {
      review.itemReviewed = organization;
    }

    return review;
  });

  return reviews;
};

export const generateAggregateRating = (
  testimonials: TestimonialSchemaInput[]
): NonStringThing | null => {
  const ratingsOnly = testimonials
    .map((t) => t.rating)
    .filter(
      (rating): rating is number =>
        typeof rating === 'number' && rating >= 1 && rating <= 5
    );

  if (ratingsOnly.length === 0) {
    return null;
  }

  const averageRating =
    ratingsOnly.reduce((sum, rating) => sum + rating, 0) / ratingsOnly.length;

  return {
    '@type': 'AggregateRating',
    ratingValue: Math.round(averageRating * 10) / 10,
    reviewCount: ratingsOnly.length,
    bestRating: 5,
    worstRating: 1,
  };
};
